import { sdkRequest } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { Button, LoadingButton } from '@eventbrite/marmalade';
import loadable from '@loadable/component';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    EventBasicInformation,
    EventBasicInformationContext,
} from '../../contexts';
import { RemindMeProps } from '../../types';
import type { SimplifiedOrganizerInfoProps } from '../_shared/OrganizerInfo/SimplifiedOrganizerInfo';
import './RemindMe.scss';

const UNREMIND_ACTION = 'unremind';
const REMIND_ACTION = 'remind';

const redirectToSignIn = (eventUrl: string) => {
    window.location.href = `/signin/confirmation/?forward=${eventUrl}`;
};

type TrackablePromise<T> = Promise<T> & {
    completed: boolean;
};

function trackPromise<T>(p: Promise<T>): TrackablePromise<T> {
    const promise = p as TrackablePromise<T>;
    promise.completed = false;
    promise.finally(() => {
        promise.completed = true;
    });
    return promise;
}

let singletonRequest: TrackablePromise<{
    action_performed: boolean;
}> | null = null;

const setReminderApi = (
    eventId: string,
    action: string,
): TrackablePromise<{ action_performed: boolean }> => {
    if (singletonRequest && !singletonRequest.completed) {
        return singletonRequest;
    }
    singletonRequest = trackPromise(
        sdkRequest(`/destination/events/${eventId}/${action}/`, {
            method: 'POST',
        }),
    );
    return singletonRequest;
};

const LazyRemindMeModal = loadable(() => import('./RemindMeModal'));

export const RemindMe = (
    props: RemindMeProps &
        SimplifiedOrganizerInfoProps & {
            organizerName?: string;
            organizerAvatar?: string;
        },
) => {
    const { id: eventId, url: eventUrl } = useContext(
        EventBasicInformationContext,
    ) as EventBasicInformation;

    const {
        isRemindedEvent,
        organizerId,
        isAuthenticated,
        organizerName,
        organizerAvatar,
    } = props;

    const [isReminded, setIsReminded] = useState(isRemindedEvent);
    const [opened, setOpened] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const setReminder = () => {
        const action = isReminded ? UNREMIND_ACTION : REMIND_ACTION;

        setIsFetching(true);

        setReminderApi(eventId, action)
            .then((response: { action_performed: boolean }) => {
                if (response.action_performed && !unmountedRef.current) {
                    if (!isReminded) {
                        setOpened(true);
                    }
                    setIsReminded(!isReminded);
                }
            })
            .finally(() => !unmountedRef.current && setIsFetching(false));
    };

    const unmountedRef = useRef(false);
    useEffect(() => {
        const urlParams = new URLSearchParams(window?.location?.search);
        const remindMeActionRequested =
            urlParams.get('remindMe') === 'true' ||
            urlParams.get('amp;remindMe') === 'true'; // TODO: remove workaround when this bug is fixed https://eventbrite.atlassian.net/browse/EB-314082
        if (isAuthenticated && !isReminded && remindMeActionRequested) {
            setReminder();
        }
        return () => {
            unmountedRef.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = () =>
        isAuthenticated
            ? setReminder()
            : redirectToSignIn(eventUrl ? eventUrl : '');

    return (
        <>
            {isFetching ? (
                <LoadingButton
                    disabled
                    isLoading
                    size="block"
                    variant="primary"
                >
                    <></>
                </LoadingButton>
            ) : (
                <Button
                    variant={isReminded ? 'secondary' : 'primary'}
                    onClick={handleClick}
                    size="block"
                    className="remind-me__button"
                >
                    {isReminded
                        ? gettext('Remove reminder')
                        : gettext('Remind me')}
                </Button>
            )}

            {opened && (
                <LazyRemindMeModal
                    onClose={() => setOpened(false)}
                    opened={opened}
                    organizerName={organizerName}
                    organizerAvatar={organizerAvatar}
                    organizerId={organizerId}
                />
            )}
        </>
    );
};

export default RemindMe;
